import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Slide,
  Fade,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchBar from '../components/SearchBar';
import FilterChips from '../components/FilterChips';
import { formatFen } from '../utils/money';

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [newProject, setNewProject] = useState({
    name: '',
    location: '',
    contractAmount: '',
    customerName: '',
    customerPhone: '',
    notes: '',
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, recvRes] = await Promise.all([
        api.get('/api/projects?pageSize=500'),
        // 回款数据失败不阻塞项目列表（应收看板是增强信息）
        api.get('/api/reports/receivables').catch(() => null),
      ]);
      const recvBy = new Map(
        ((recvRes?.data?.projects) || []).map(r => [r.projectId, r])
      );
      const list = response.data.data || response.data;
      setProjects(list.map(p => ({
        ...p,
        receivedAmount: recvBy.get(p.id)?.received ?? null,
        receivableAmount: recvBy.get(p.id)?.receivable ?? null,
        collectionRate: recvBy.get(p.id)?.rate ?? null,
      })));
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error.response?.data?.error || error.message || '加载项目列表失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreateProject = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const project = {
          id: Date.now().toString(),
          ...newProject,
          contractAmount: Math.round(parseFloat(newProject.contractAmount || 0) * 100),
          status: 0,
          type: '',
        };
        await api.post('/api/projects', project);
        setOpenDialog(false);
        setNewProject({
          name: '',
          location: '',
          contractAmount: '',
          customerName: '',
          customerPhone: '',
          notes: '',
        });
        fetchProjects();
      } catch (error) {
        console.error('Error creating project:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (project) => {
    setEditingProject(project);
    setNewProject({
      name: project.name || '',
      location: project.location || '',
      contractAmount: (project.contractAmount / 100).toString(),
      customerName: project.customerName || '',
      customerPhone: project.customerPhone || '',
      notes: project.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const project = {
          ...newProject,
          contractAmount: Math.round(parseFloat(newProject.contractAmount || 0) * 100),
          status: editingProject.status,
          type: editingProject.type || '',
        };
        await api.put(`/api/projects/${editingProject.id}`, project);
        setOpenDialog(false);
        setEditingProject(null);
        setNewProject({ name: '', location: '', contractAmount: '', customerName: '', customerPhone: '', notes: '' });
        fetchProjects();
      } catch (error) {
        console.error('Error updating project:', error);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/projects/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 0: return 'default';
      case 1: return 'primary';
      case 2: return 'success';
      case 3: return 'warning';
      case 4: return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0: return '未开工';
      case 1: return '施工中';
      case 2: return '已完工';
      case 3: return '已结算';
      case 4: return '暂停';
      default: return '未知';
    }
  };


  // 筛选和搜索逻辑
  const filteredProjects = projects.filter((project) => {
    // 状态筛选
    if (filterStatus !== 'all' && project.status !== parseInt(filterStatus)) {
      return false;
    }
    // 搜索过滤
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        project.name?.toLowerCase().includes(query) ||
        project.location?.toLowerCase().includes(query) ||
        project.customerName?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>项目管理</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={fetchProjects}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* 页面标题和新建按钮 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>项目管理</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ minHeight: 44 }}
        >
          新建项目
        </Button>
      </Box>
  
      {/* 搜索栏和筛选标签 */}
      <Box sx={{ mb: 3 }}>
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="搜索项目名称、地点或客户..."
        />
        <Box sx={{ mt: 2 }}>
          <FilterChips
            options={[
              { label: '全部', value: 'all' },
              { label: '未开工', value: '0' },
              { label: '施工中', value: '1' },
              { label: '已完工', value: '2' },
              { label: '已结算', value: '3' },
              { label: '暂停', value: '4' },
            ]}
            selectedValue={filterStatus}
            onChange={setFilterStatus}
          />
        </Box>
      </Box>
  
      {/* 项目卡片网格 */}
      {filteredProjects.length === 0 ? (
        <Fade in={true}>
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" color="text.secondary">
                {projects.length === 0 ? '暂无项目' : '没有匹配的项目'}
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 1 }}>
                {projects.length === 0 ? '点击“新建项目”创建第一个项目' : '尝试其他搜索条件'}
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      ) : (
        <Grid container spacing={2.5}>
          {filteredProjects.map((project, index) => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Slide direction="up" in={true} style={{ transitionDelay: `${index * 50}ms` }}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      borderColor: 'grey.300',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                        {project.name}
                      </Typography>
                      <Box>
                        <Chip 
                          label={getStatusText(project.status)} 
                          color={getStatusColor(project.status)} 
                          size="small" 
                          sx={{ mr: 0.5, fontWeight: 600 }} 
                        />
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); handleEditStart(project); }}
                          sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => { e.stopPropagation(); handleDelete(project.id); }}
                          sx={{ '&:hover': { backgroundColor: 'error.lighter' } }}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                      
                    {project.location && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {project.location}
                        </Typography>
                      </Box>
                    )}
                      
                    {project.customerName && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {project.customerName}
                        </Typography>
                      </Box>
                    )}
                      
                    {project.customerPhone && (
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {project.customerPhone}
                        </Typography>
                      </Box>
                    )}
                      
                    <Box 
                      sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: '1px solid', 
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        合同金额
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight={700}>
                        {formatFen(project.contractAmount)}
                      </Typography>
                    </Box>

                    {project.receivedAmount != null && (
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          已收 {project.collectionRate != null ? `(${project.collectionRate}%)` : ''}
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {formatFen(project.receivedAmount)}
                        </Typography>
                      </Box>
                    )}
                    {project.receivableAmount != null && project.receivableAmount !== 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color={project.receivableAmount > 0 ? 'warning.dark' : 'success.main'}>
                          {project.receivableAmount > 0 ? '甲方尚欠' : '超收'}
                        </Typography>
                        <Typography variant="body1" fontWeight={700} color={project.receivableAmount > 0 ? 'warning.dark' : 'success.main'}>
                          {formatFen(project.receivableAmount)}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingProject(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingProject ? '编辑项目' : '新建项目'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="项目名称"
            fullWidth
            variant="outlined"
            value={newProject.name}
            onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="项目地点"
            fullWidth
            variant="outlined"
            value={newProject.location}
            onChange={(e) => setNewProject({ ...newProject, location: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="合同金额（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={newProject.contractAmount}
            onChange={(e) => setNewProject({ ...newProject, contractAmount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="客户名称"
            fullWidth
            variant="outlined"
            value={newProject.customerName}
            onChange={(e) => setNewProject({ ...newProject, customerName: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="客户电话"
            fullWidth
            variant="outlined"
            value={newProject.customerPhone}
            onChange={(e) => setNewProject({ ...newProject, customerPhone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newProject.notes}
            onChange={(e) => setNewProject({ ...newProject, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingProject(null); }}>取消</Button>
          <Button onClick={editingProject ? handleEditSave : handleCreateProject} variant="contained" disabled={!newProject.name}>
            {editingProject ? '保存' : '创建'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定删除此项目？删除后不可恢复。"
      />
    </Box>
  );
}
